/**
 * Production-safe logging utility
 * Logs to console in development, silent in production (except errors)
 * Includes secure logging functions to prevent sensitive data exposure
 */

const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Fields that should never be logged (case-insensitive)
 */
const SENSITIVE_FIELDS = [
  'password', 'pwd', 'passwd', 'secret', 'token', 'jwt', 'auth', 'key', 'api_key',
  'session', 'cookie', 'email', 'user_id', 'id', 'ssn', 'social', 'credit',
  'card', 'account', 'pin', 'otp', 'recovery', 'backup'
];

/**
 * Sanitize data for safe logging by removing/masking sensitive fields
 * @param {any} data - Data to sanitize
 * @param {object} options - Sanitization options
 * @returns {any} Sanitized data safe for logging
 */
const sanitizeForLogging = (data, options = {}) => {
  const { 
    maskLength = 4, 
    replacement = '[REDACTED]',
    preserveStructure = true 
  } = options;

  if (data === null || data === undefined) return data;

  // Handle primitive types
  if (typeof data !== 'object') return data;

  // Handle arrays
  if (Array.isArray(data)) {
    return data.map(item => sanitizeForLogging(item, options));
  }

  // Handle objects
  const sanitized = preserveStructure ? {} : { ...data };
  
  for (const [key, value] of Object.entries(data)) {
    const keyLower = key.toLowerCase();
    const isSensitive = SENSITIVE_FIELDS.some(field => keyLower.includes(field));
    
    if (isSensitive) {
      if (typeof value === 'string' && value.length > maskLength) {
        sanitized[key] = value.substring(0, maskLength) + '***';
      } else {
        sanitized[key] = replacement;
      }
    } else if (typeof value === 'object') {
      sanitized[key] = sanitizeForLogging(value, options);
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
};

/**
 * Log debug information (development only)
 */
export const logDebug = (...args) => {
  if (isDevelopment) {
    const sanitizedArgs = args.map(arg => sanitizeForLogging(arg));
    // eslint-disable-next-line no-console
    console.log('[DEBUG]', ...sanitizedArgs);
  }
};

/**
 * Secure debug logging that automatically sanitizes sensitive data
 */
export const logDebugSecure = (message, data = null, options = {}) => {
  if (isDevelopment) {
    const sanitizedData = data ? sanitizeForLogging(data, options) : null;
    // eslint-disable-next-line no-console
    console.log('[DEBUG]', message, sanitizedData);
  }
};

/**
 * Log informational messages (development only)
 */
export const logInfo = (...args) => {
  if (isDevelopment) {
    // eslint-disable-next-line no-console
    console.info('[INFO]', ...args);
  }
};

/**
 * Log warnings (development only)
 */
export const logWarning = (...args) => {
  if (isDevelopment) {
    // eslint-disable-next-line no-console
    console.warn('[WARNING]', ...args);
  }
};

/**
 * Log errors (always logged, even in production)
 * In production, consider sending to error tracking service (e.g., Sentry)
 */
export const logError = (message, error, context = {}) => {
  // Sanitize context to avoid logging sensitive data in error reports
  const sanitizedContext = sanitizeForLogging(context);
  
  // Always log errors to console
  // eslint-disable-next-line no-console
  console.error('[ERROR]', message, error.message || error, sanitizedContext);

  // In production, you could send to error tracking service
  if (!isDevelopment && window.Sentry) {
    window.Sentry.captureException(error, {
      contexts: {
        custom: sanitizedContext
      },
      tags: {
        source: 'frontend'
      }
    });
  }
};

/**
 * Secure error logging for database/API errors that might contain sensitive data
 */
export const logErrorSecure = (message, error, context = {}, options = {}) => {
  const sanitizedContext = sanitizeForLogging(context, options);
  const sanitizedError = sanitizeForLogging({
    message: error.message,
    name: error.name,
    code: error.code,
    status: error.status
  }, options);
  
  // eslint-disable-next-line no-console
  console.error('[ERROR]', message, sanitizedError, sanitizedContext);

  // In production, send sanitized error to tracking service
  if (!isDevelopment && window.Sentry) {
    window.Sentry.captureException(new Error(message), {
      contexts: {
        custom: sanitizedContext,
        error: sanitizedError
      },
      tags: {
        source: 'frontend',
        type: 'secure'
      }
    });
  }
};

/**
 * Log performance metrics (development only)
 */
export const logPerformance = (label, duration) => {
  if (isDevelopment) {
    // eslint-disable-next-line no-console
    console.log(`[PERF] ${label}: ${duration.toFixed(2)}ms`);
  }
};

/**
 * Create a performance timer
 */
export const createTimer = (label) => {
  const start = performance.now();

  return {
    end: () => {
      const duration = performance.now() - start;
      logPerformance(label, duration);
      return duration;
    }
  };
};

/**
 * Log table for better formatting (development only)
 */
export const logTable = (data) => {
  if (isDevelopment && console.table) {
    // eslint-disable-next-line no-console
    console.table(data);
  }
};

/**
 * Log grouped data (development only)
 */
export const logGroup = (label, callback) => {
  if (isDevelopment) {
    // eslint-disable-next-line no-console
    console.group(label);
    callback();
    // eslint-disable-next-line no-console
    console.groupEnd();
  }
};

/**
 * Database operation logging with automatic data sanitization
 */
export const logDatabaseOperation = (operation, table, data = null, result = null) => {
  if (isDevelopment) {
    const sanitizedData = data ? sanitizeForLogging(data) : null;
    const sanitizedResult = result ? sanitizeForLogging(result) : null;
    
    // eslint-disable-next-line no-console
    console.log(`[DB] ${operation.toUpperCase()} ${table}`, sanitizedData, sanitizedResult);
  }
};

/**
 * Authentication event logging (development only, sanitized)
 */
export const logAuthEvent = (event, details = {}) => {
  if (isDevelopment) {
    const sanitizedDetails = sanitizeForLogging(details, { 
      maskLength: 2,
      replacement: '[HIDDEN]'
    });
    
    // eslint-disable-next-line no-console
    console.log(`[AUTH] ${event}`, sanitizedDetails);
  }
};

const logger = {
  // Standard logging
  debug: logDebug,
  info: logInfo,
  warning: logWarning,
  error: logError,
  performance: logPerformance,
  timer: createTimer,
  table: logTable,
  group: logGroup,

  // Secure logging functions
  debugSecure: logDebugSecure,
  errorSecure: logErrorSecure,
  database: logDatabaseOperation,
  auth: logAuthEvent,
  sanitize: sanitizeForLogging
};

export default logger;
